// src/App.jsx - Updated with all routes and SEO
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { OnboardingProvider } from './contexts/OnboardingProvider';

import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import LegalDocument from './components/LegalDocument';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Posts from './pages/Posts';
import Billing from './pages/Billing';
import About from './pages/About';
import Contact from './pages/Contact';
import AdminPortal from './pages/AdminPortal';
import Documentation from './pages/Documentation';
import Help from './pages/Help';
import Status from './pages/Status';
import Community from './pages/Community';
import Careers from './pages/Careers';
import Blog from './pages/Blog';
import Integrations from './pages/Integrations';
import API from './pages/API';
import Unsubscribe from './pages/Unsubscribe';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

import './index.css';

import ScrollToTop from "./components/ScrollToTop.jsx";
import AdminRoute from "./components/auth/AdminRoute.jsx";
import ModerationDashboard from "./components/admin/ModerationDashboard.jsx";
import CookieConsent from './components/CookieConsent';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

const helmetContext = {};

function DebugComponent() {
    const { token } = useParams();  // ← Now this runs when component renders
    const searchParams = new URLSearchParams(window.location.search);

    console.log('useParams() result:', useParams());
    console.log('token:', token);

    return (
        <div style={{ padding: '20px', background: '#f0f0f0' }}>
            <h2>Route Debug Info</h2>
            <p><strong>Route Parameter (token):</strong> {token || 'Not found'}</p>
            <p><strong>Query Parameters:</strong> {window.location.search || 'None'}</p>
            <p><strong>Full URL:</strong> {window.location.href}</p>
            <p><strong>useParams() object:</strong> {JSON.stringify(useParams())}</p>
            <hr />
            <p>Test URLs:</p>
            <ul>
                <li><a href="/debug/test123">Route param: /debug/test123</a></li>
                <li><a href="/debug/test123?query=value">Both: /debug/test123?query=value</a></li>
            </ul>
        </div>
    );
}


function App() {
    return (
        <HelmetProvider context={helmetContext}>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <OnboardingProvider>
                        <Router
                            future={{
                                v7_startTransition: true,
                                v7_relativeSplatPath: true,
                            }}
                        >
                            <div className="min-h-screen bg-gray-50">
                                <ScrollToTop />
                                <Routes>

                                    <Route path="/debug/:token" element={<DebugComponent />} />

                                    {/* Password recovery routes */}
                                    <Route path='/reset-password/:token' element={<ResetPassword />} />
                                    <Route path='/forgot-password' element={<ForgotPassword />} />

                                    {/* Public routes */}
                                    <Route path="/" element={<Landing />} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/register" element={<Register />} />





                                    {/* Company pages */}
                                    <Route path="/about" element={<About />} />
                                    <Route path="/contact" element={<Contact />} />
                                    <Route path="/careers" element={<Careers />} />
                                    <Route path="/blog" element={<Blog />} />

                                    {/* Legal pages */}
                                    <Route
                                        path="privacy"
                                        element={
                                            <LegalDocument
                                                documentType="privacy"
                                                title="Privacy Policy"
                                                description="How we collect, use, and protect your personal information"
                                            />
                                        }
                                    />
                                    <Route
                                        path="terms"
                                        element={
                                            <LegalDocument
                                                documentType="terms"
                                                title="Terms of Service"
                                                description="Terms and conditions for using our website and services"
                                            />
                                        }
                                    />

                                    <Route
                                        path="gdpr"
                                        element={
                                            <LegalDocument
                                                documentType="gdpr"
                                                title="GDPR"
                                                description="GDPR conditions for using our website and services"
                                            />
                                        }
                                    />
                                    <Route
                                        path="cookie"
                                        element={
                                            <LegalDocument
                                                documentType="cookie"
                                                title="Cookies Policy"
                                                description="Cookies Policy for using our website and services"
                                            />
                                        }
                                    />
                                    <Route
                                        path="eula"
                                        element={
                                            <LegalDocument
                                                documentType="eula"
                                                title="End User License Agreement"
                                                description="End User License Agreement for using our website and services"
                                            />
                                        }
                                    />


                                    {/* Resource pages */}
                                    <Route path="/docs" element={<Documentation />} />
                                    <Route path="/help" element={<Help />} />
                                    <Route path="/community" element={<Community />} />
                                    <Route path="/status" element={<Status />} />

                                    {/* Product pages */}
                                    <Route path="/integrations" element={<Integrations />} />
                                    <Route path="/api" element={<API />} />

                                    {/* Features and Pricing redirect to landing page sections */}
                                    <Route path="/features" element={<Landing />} />
                                    <Route path="/pricing" element={<Landing />} />

                                    {/* Protected routes */}
                                    <Route path="/dashboard" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Dashboard />
                                            </Layout>
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/posts" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Posts />
                                            </Layout>
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/settings" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Settings />
                                            </Layout>
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/billing" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Billing />
                                            </Layout>
                                        </ProtectedRoute>
                                    } />

                                    {/* 404 page */}
                                    <Route path="*" element={<NotFound />} />

                                    {/* Admin-only route */}
                                    <Route
                                        path="/admin"
                                        element={
                                            <AdminRoute>
                                                <Layout>
                                                    <AdminPortal />
                                                </Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    <Route
                                        path="/admin/moderation"
                                        element={
                                            <AdminRoute>
                                                <Layout>
                                                    <ModerationDashboard />
                                                </Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

                                </Routes>

                                <Toaster
                                    position="top-right"
                                    toastOptions={{
                                        duration: 4000,
                                        style: {
                                            background: '#363636',
                                            color: '#fff',
                                        },
                                    }}
                                />
                                <CookieConsent />
                            </div>
                        </Router>
                    </OnboardingProvider>
                </AuthProvider>
            </QueryClientProvider>
        </HelmetProvider>
    );
}

// 404 Component
function NotFound() {
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center">
            <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Page Not Found</h2>
                <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
                <a
                    href="/"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    Go Home
                </a>
            </div>
        </div>
    );
}

export default App;